import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../components/cart/cart-context';
import Price from '../components/price';
import Image from '../components/Image';
import LoadingDots from '../components/loading-dots';
import { DEFAULT_OPTION } from 'lib/constants';
import LogoSquare from '../components/logo-square';

export default function ShippingPage() {
  const { cart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [shippingMethod, setShippingMethod] = useState('economy');

  // Récupérer les données du formulaire depuis l'état ou localStorage
  const formData = location.state || JSON.parse(localStorage.getItem('checkoutFormData') || '{}');

  if (!cart || cart.lines.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-neutral-900 dark:text-white">
            Votre panier est vide
          </h1>
          <Link
            to="/"
            className="inline-block rounded-md bg-blue-600 px-6 py-3 text-white hover:bg-blue-700"
          >
            Continuer les achats
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Sauvegarder les données de shipping
    const shippingData = {
      shippingMethod,
      shippingPrice: shippingOptions.find(o => o.id === shippingMethod)?.price || '0'
    };
    
    localStorage.setItem('shippingData', JSON.stringify(shippingData));
    
    // Rediriger vers la page de paiement
    navigate('/payment', { state: { ...formData, shippingData } });
    setLoading(false);
  };

  const handleBackToInformation = () => {
    navigate('/checkout', { state: formData });
  };

  // Construire l'adresse complète
  const fullAddress = [
    formData.address,
    formData.apartment && `Appartement ${formData.apartment}`,
    formData.city,
    formData.state,
    formData.postalCode,
    formData.country
  ].filter(Boolean).join(', ');

  const shippingOptions = [
    {
      id: 'economy',
      name: 'Économique',
      description: '5 à 8 jours ouvrables',
      price: '4.90',
      currencyCode: cart.cost.totalAmount.currencyCode
    },
    {
      id: 'standard',
      name: 'Standard',
      description: '3 à 4 jours ouvrables',
      price: '6.90',
      currencyCode: cart.cost.totalAmount.currencyCode
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div className="mx-auto flex max-w-7xl">
        {/* Formulaire de shipping - Colonne gauche */}
        <div className="flex-1 border-r border-neutral-200 dark:border-neutral-800">
          <div className="mx-auto max-w-2xl px-6 py-12">
            {/* Logo ShopLink */}
            <div className="mb-4 flex justify-start">
              <Link to="/">
                <LogoSquare />
              </Link>
            </div>
            
            {/* Breadcrumb */}
            <div className="mb-8 flex items-center justify-start space-x-2 text-sm text-neutral-500 dark:text-neutral-400">
              <Link
                to="/checkout"
                className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                onClick={handleBackToInformation}
              >
                Information
              </Link>
              <span>/</span>
              <span className="font-medium text-neutral-900 dark:text-white">Livraison</span>
              <span>/</span>
              <span>Paiement</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Résumé Contact et Ship to */}
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-md border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
                  <div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Contact</p>
                    <p className="mt-1 text-sm font-medium text-neutral-900 dark:text-white">
                      {formData.email || formData.phoneNumber ? (
                        formData.email || `${formData.phoneCode} ${formData.phoneNumber}`
                      ) : (
                        'Non renseigné'
                      )}
                    </p>
                  </div>
                  <Link
                    to="/checkout"
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    onClick={handleBackToInformation}
                  >
                    Modifier
                  </Link>
                </div>

                <div className="flex items-start justify-between rounded-md border border-neutral-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-800">
                  <div className="flex-1">
                    <p className="text-sm text-neutral-500 dark:text-neutral-400">Livrer à</p>
                    <p className="mt-1 text-sm font-medium text-neutral-900 dark:text-white">
                      {fullAddress || 'Non renseigné'}
                    </p>
                  </div>
                  <Link
                    to="/checkout"
                    className="ml-4 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    onClick={handleBackToInformation}
                  >
                    Modifier
                  </Link>
                </div>
              </div>

              {/* Section Shipping Method */}
              <div>
                <h2 className="mb-4 text-lg font-semibold text-neutral-900 dark:text-white">
                  Méthode de livraison
                </h2>
                <div className="space-y-0 divide-y divide-neutral-200 dark:divide-neutral-700">
                  {shippingOptions.map((option) => (
                    <label
                      key={option.id}
                      className="flex cursor-pointer items-center justify-between p-4 hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
                    >
                      <div className="flex items-center space-x-4">
                        <input
                          type="radio"
                          name="shippingMethod"
                          value={option.id}
                          checked={shippingMethod === option.id}
                          onChange={(e) => setShippingMethod(e.target.value)}
                          className="h-4 w-4 border-neutral-300 text-blue-600 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-700"
                        />
                        <div>
                          <p className="text-sm font-medium text-neutral-900 dark:text-white">
                            {option.name}
                          </p>
                          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                            {option.description}
                          </p>
                        </div>
                      </div>
                      <Price
                        amount={option.price}
                        currencyCode={option.currencyCode}
                        className="text-sm font-medium"
                      />
                    </label>
                  ))}
                </div>
              </div>

              {/* Navigation buttons */}
              <div className="flex items-center justify-between pt-4">
                <Link
                  to="/checkout"
                  onClick={handleBackToInformation}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  ← Retour aux informations
                </Link>
                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-md bg-blue-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <LoadingDots className="bg-white" /> : 'Continuer vers le paiement'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Order Summary - Colonne droite */}
        <div className="w-96 border-l border-neutral-200 dark:border-neutral-800">
          <div className="sticky top-0 h-screen overflow-y-auto px-6 py-12">
            <h2 className="mb-6 text-lg font-semibold text-neutral-900 dark:text-white">
              Résumé de commande
            </h2>

            <div className="space-y-4">
              {cart.lines.map((item, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="relative h-16 w-16 shrink-0">
                    <div className="h-full w-full overflow-hidden rounded-md border-2 border-blue-500 bg-neutral-100 dark:bg-neutral-800">
                      <Image
                        src={item.merchandise.product.featuredImage?.url}
                        alt={item.merchandise.product.title}
                        width={64}
                        height={64}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    {item.quantity > 1 && (
                      <div className="absolute -right-1 -top-1 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-medium text-white shadow-sm">
                        {item.quantity}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                      {item.merchandise.product.title}
                    </p>
                    {item.merchandise.title !== DEFAULT_OPTION && (
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                        {item.merchandise.title}
                      </p>
                    )}
                    <Price
                      amount={item.cost.totalAmount.amount}
                      currencyCode={item.cost.totalAmount.currencyCode}
                      className="mt-1 text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-3 border-t border-neutral-200 pt-6 dark:border-neutral-700">
              <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
                <span>Sous-total</span>
                <Price
                  amount={cart.cost.subtotalAmount.amount}
                  currencyCode={cart.cost.subtotalAmount.currencyCode}
                />
              </div>
              <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
                <span>Livraison</span>
                <Price
                  amount={shippingOptions.find(o => o.id === shippingMethod)?.price || '0'}
                  currencyCode={cart.cost.totalAmount.currencyCode}
                />
              </div>
              <div className="flex justify-between border-t border-neutral-200 pt-3 text-xl font-semibold text-neutral-900 dark:border-neutral-700 dark:text-white">
                <span>Total</span>
                <Price
                  amount={(
                    parseFloat(cart.cost.totalAmount.amount) +
                    parseFloat(shippingOptions.find(o => o.id === shippingMethod)?.price || '0')
                  ).toString()}
                  currencyCode={cart.cost.totalAmount.currencyCode}
                  className="text-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

